// React
import React from "react";
// Constants
import { LabelConstants } from "@ms/planner-generic-controls/lib/local/constants/LabelConstants";
import { getLabelPlaceholderByIndex } from "@ms/planner-models/lib/local/utilities/LabelUtilities";
import { TaskGridColumnId } from "@ms/uno-constants/lib/local/TaskGridConstants";
// Controls
import { LabelTag } from "@ms/planner-generic-controls/lib/local/labelTag/LabelTag";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Utilities
export { getLabelPlaceholder } from "@ms/planner-models/lib/local/utilities/LabelUtilities";
/** Resolve a grid column ID to its localized display label. Falls back to the raw column ID for custom/unknown columns. */ export function resolveColumnLabel(colId) {
    const COLUMN_ID_LABELS = {
        [TaskGridColumnId.MarkTaskAsCompleteColumnName]: "",
        [TaskGridColumnId.Title]: Strings.Task_TitleFieldText,
        [TaskGridColumnId.Status]: Strings.Task_StatusFieldText,
        [TaskGridColumnId.Priority]: Strings.Task_PriorityFieldText,
        [TaskGridColumnId.Bucket]: Strings.Task_BucketIdFieldText,
        [TaskGridColumnId.Plan]: Strings.Plan_PlanColumnHeaderText,
        [TaskGridColumnId.StartDate]: Strings.Task_StartFieldText,
        [TaskGridColumnId.FinishDate]: Strings.Task_FinishFieldText,
        [TaskGridColumnId.DueDate]: Strings.Task_DueFieldText,
        [TaskGridColumnId.Labels]: Strings.Task_LabelsFieldText,
        [TaskGridColumnId.Assignments]: Strings.Task_AssignedToFieldText,
        [TaskGridColumnId.QuickLook]: Strings.Task_QuickLookFieldText,
        [TaskGridColumnId.Predecessor]: Strings.Task_PredecessorFieldText,
        [TaskGridColumnId.Successor]: Strings.Task_SuccessorFieldText,
        [TaskGridColumnId.Goal]: Strings.Task_GoalsFieldText
    };
    return COLUMN_ID_LABELS[colId] ?? colId;
}
/**
 * Utilities to help with label-related UI code
 */ /**
 * Build label list from plan categories.
 * Maps plan categories to ILabel objects with proper formatting and placeholder text.
 * When `labelUsageCounts` is provided, each label's `usageCount` is stamped from the map
 * so the picker can surface labels already in use elsewhere in the plan at the top
 * (matching UNO behavior). The map is expected to come from a pre-aggregated source
 * such as `ITaskStore.getLabelUsageCountsForPlan` — this function does no iteration over tasks.
 * @param plan The plan containing categories
 * @param [labelUsageCounts] Pre-aggregated map of category id -> usage count
 * @returns Array of ILabel objects, or empty array if no categories exist
 */ export function buildLabelsFromPlanCategories(plan, labelUsageCounts) {
    const categories = plan?.planView?.categories ?? [];
    if (categories.length === 0) {
        return [];
    }
    const labels = categories.map((category, index)=>{
        const id = category.id;
        const placeholderText = getLabelPlaceholderByIndex(index);
        return {
            id,
            index,
            name: category.displayName,
            labelFormatting: LabelConstants[index],
            placeholderText,
            ...labelUsageCounts ? {
                usageCount: labelUsageCounts.get(id) ?? 0
            } : {}
        };
    });
    return labels;
}
/**
 * Stable partition into the same four buckets that `sortLabelsByUsageAndIndex` produces, **without**
 * the index pre-sort. Callers that already have items in their desired secondary order (e.g. the
 * canvas `LabelsEditor` consumes labels already in category order) should call this directly.
 * Generic over any shape with `id` and optional `usageCount` so both `ILabel` and `ILabelOption`
 * callers share the same bucketing logic.
 *
 * Bucket order:
 * 1. Applied & used (usageCount > 0)
 * 2. Applied & unused
 * 3. Not applied & used
 * 4. Not applied & unused
 *
 * @param items Items to partition; bucket order preserves input order.
 * @param [appliedIds] Optional set of currently applied item ids.
 */ export function partitionByUsageAndApplied(items, appliedIds) {
    if (!items || items.length === 0) {
        return [];
    }
    const appliedSet = new Set(appliedIds);
    const appliedUsed = [];
    const appliedUnused = [];
    const notAppliedUsed = [];
    const notAppliedUnused = [];
    for (const item of items){
        const isApplied = appliedSet.has(item.id);
        const isUsed = (item.usageCount ?? 0) > 0;
        if (isApplied) {
            (isUsed ? appliedUsed : appliedUnused).push(item);
        } else {
            (isUsed ? notAppliedUsed : notAppliedUnused).push(item);
        }
    }
    return appliedUsed.concat(appliedUnused, notAppliedUsed, notAppliedUnused);
}
/**
 * Sort labels for suggestion UI.
 * Ordering rules (stable within each bucket, by index):
 * 1. Applied & used (usageCount > 0)
 * 2. Applied & unused (usageCount <= 0)  // rare but supported
 * 3. Not applied & used
 * 4. Not applied & unused
 *
 * This implementation performs:
 *  - One defensive copy
 *  - One sort by index
 *  - One linear partition pass (delegated to `partitionByUsageAndApplied`)
 *
 * @param labels All labels to consider
 * @param [appliedLabelIds] Optional set of currently applied label ids
 */ export function sortLabelsByUsageAndIndex(labels, appliedLabelIds) {
    if (!labels || labels.length === 0) {
        return [];
    }
    // Single sort by index first (guarantees each bucket is index-ordered without re-sorting).
    const sorted = [
        ...labels
    ].sort(compareLabelsByIndex);
    return partitionByUsageAndApplied(sorted, appliedLabelIds);
}
/**
 * Get the indices of labels that have been applied to a task.
 * Maps applied label IDs to their corresponding index positions in the task's plan labels array.
 * @param appliedLabels Array of label IDs that are currently applied to the task
 * @param taskPlanLabels Array of all available labels in the task's plan
 * @returns Array of zero-based indices for labels that are applied
 */ export function getAppliedLabelIndices(appliedLabelIds, taskPlanLabels) {
    if (appliedLabelIds.length === 0 || taskPlanLabels.length === 0) {
        return [];
    }
    const appliedIds = new Set(appliedLabelIds);
    return taskPlanLabels.flatMap((label, index)=>appliedIds.has(label.id) ? [
            index
        ] : []);
}
/**
 * Get label ids that correspond to applied index values.
 * Maps applied indices to the label ids present in the task plan label list.
 * @param appliedLabelIndices Array of zero-based label indices that are applied to the task
 * @param taskPlanLabels Array of available labels for the task's plan
 * @returns Label ids that are currently applied
 */ export function getAppliedLabelIds(appliedLabelIndices, taskPlanLabels) {
    if (appliedLabelIndices.length === 0 || taskPlanLabels.length === 0) {
        return [];
    }
    const appliedIds = new Set(appliedLabelIndices);
    return taskPlanLabels.filter((_, index)=>appliedIds.has(index)).map((label)=>label.id);
}
/**
 * Compare labels by index number only
 * @param label1 The first label to compare
 * @param label2 The second label to compare
 */ export function compareLabelsByIndex(label1, label2) {
    return label1.index === label2.index ? 0 : label1.index < label2.index ? -1 : 1;
}
/**
 * Convert ILabel array to ILabelOption array for use with LabelsEditor.
 * Maps the full ILabel model to the lighter-weight ILabelOption interface.
 */ export function labelsToOptions(labels) {
    return labels.map((label)=>({
            id: label.id,
            name: label.name || label.placeholderText,
            color: label.labelFormatting.backgroundColor,
            textColor: label.labelFormatting.textColor,
            hoverBorderColor: label.labelFormatting.hoverBorderColor,
            hoverBackgroundColor: label.labelFormatting.hoverBackgroundColor,
            usageCount: label.usageCount
        }));
}
/**
 * Renders a small color swatch for the category header
 */ export function renderCategoryHeaderImage(categories, categoryIndex) {
    const cat = categories[categoryIndex];
    const colors = cat?.extensions?.categoryFormatting ?? LabelConstants[categoryIndex];
    if (!colors) return null;
    const tooltipContent = cat?.displayName?.trim() || getLabelPlaceholderByIndex(categoryIndex);
    return /*#__PURE__*/ React.createElement(LabelTag, {
        isCollapsed: false,
        title: "",
        showBorder: true,
        labelColors: colors,
        tooltipContent: tooltipContent
    });
}
